import { createPortal } from 'react-dom';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import type { Tour } from './tourSteps';

type Props = {
  tour: Tour;
  onStart: () => void;
  onSkip: () => void;
};

/**
 * Offered once per login: start the walkthrough or skip it. Both choices are
 * recorded against the current login, so it never re-asks inside a session.
 */
function TourPrompt({ tour, onStart, onSkip }: Props) {
  return createPortal(
    <div
      className="ktour-promptwrap"
      role="dialog"
      aria-modal="true"
      aria-label="Product tour"
    >
      <div className="ktour-prompt">
        <div className="ktour-prompt-head">
          <span className="ktour-eyebrow">
            <AutoAwesomeRoundedIcon /> GUIDED TOUR
          </span>
          <h3>{tour.intro.title}</h3>
          <p>{tour.intro.body}</p>
        </div>

        <div className="ktour-prompt-body">
          <p className="ktour-what">What you'll do</p>
          <ol className="ktour-steplist">
            {tour.intro.bullets.map((b, i) => (
              <li key={b.label}>
                <i>{i + 1}</i>
                <div className="ktour-steptext">
                  <b>{b.label}</b>
                  {b.caption && <span>{b.caption}</span>}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="ktour-prompt-foot">
          {tour.intro.duration && (
            <span className="ktour-duration">
              <ScheduleRoundedIcon />
              {tour.intro.duration}
            </span>
          )}
          <span className="ktour-prompt-actions">
            <button type="button" className="ktour-btn ghost" onClick={onSkip}>
              Skip for now
            </button>
            <button
              type="button"
              className="ktour-btn primary"
              onClick={onStart}
              autoFocus
            >
              <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
              Start the tour
            </button>
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default TourPrompt;
